// uiFeedback — common UI helpers for outcome formatting / permission bitflag
// conversion (Phase 6 Task 8).
//
// Why a separate module:
// - `formatMs` was duplicated in `loginForm.ts` / `roomSettingsForm.ts` /
//   `createRoomForm.ts` (Phase 6 Task 7 申し送り). This module collects the
//   shared logic so future tweaks (i18n, units) happen in one place.
// - permissions bitflag <-> {kick, imagepaste} 変換は AdminPage の credential
//   発行 form / 一覧表示の両方で使う. KICK / IMAGEPASTE の扱いは MVP 共通な
//   ので、application 層に閉じた純粋関数として切り出す.
//
// Notes:
// - `Perm.KICK = 1 << 0`, `Perm.IMAGEPASTE = 1 << 1`, `Perm.CLEAR = 1 << 2`,
//   `ALL_PERMS = 7` (Phase 7 Task 7-5 で CLEAR 追加、`@oekaki/protocol` の
//   `shared/permissions.ts`). server も同 bitflag をそのまま受領
//   (`CredentialPermissionsSchema = z.number().int().gte(0).lte(ALL_PERMS)`).
// - permissionsBitflagFromBooleans は CB 入力 (kick / imagepaste / clear) を
//   `Perm.*` の OR で合成する. 8 通り (0..7) の cross-product を吐く.
// - permissionsBooleansFromBitflag は逆変換 (一覧表示でバッジを出すため).

import { ALL_PERMS, hasPerm, Perm } from '@oekaki/protocol';

/* -----------------------------------------------------------------------------
 * formatMs — humanize a milliseconds duration
 *
 * Used by `mapPatchRoomOutcome` / `mapSetOwnerEmailOutcome` /
 * `mapLoginByOwnerPasswordOutcome` etc. to render `retryAfterMs` as a 「N 秒後」
 * "N 分後" "N 時間 M 分後" string. Ceiling 秒 (rounding up; users are happier
 * waiting one extra second than refreshing prematurely).
 * --------------------------------------------------------------------------- */

export const formatMs = (ms: number): string => {
  const sec = Math.max(1, Math.ceil(ms / 1_000));
  if (sec >= 60 * 60) {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    return minutes > 0 ? `${hours}時間${minutes}分` : `${hours}時間`;
  }
  if (sec >= 60) {
    const mins = Math.floor(sec / 60);
    const remSec = sec % 60;
    return remSec > 0 ? `${mins}分${remSec}秒` : `${mins}分`;
  }
  return `${sec}秒`;
};

/* -----------------------------------------------------------------------------
 * Permission bitflag <-> {kick, imagepaste} booleans
 * --------------------------------------------------------------------------- */

export interface PermissionBooleans {
  /** `Perm.KICK` が立っているか. */
  readonly kick: boolean;
  /** `Perm.IMAGEPASTE` が立っているか. */
  readonly imagepaste: boolean;
  /**
   * `Perm.CLEAR` が立っているか (Phase 7 Task 7-5).
   *
   * `room.clearOpen` が `true` のルームでは無権限ユーザでも clear 可能なので、
   * 本フラグを credential に付ける必要は無い. `clearOpen=false` (default) の
   * ルームで「特定の credential ユーザだけに clear 権限を渡したい」用途で使う.
   */
  readonly clear: boolean;
}

/**
 * CB 3 つ (kick / imagepaste / clear) を server が受け取る bitflag に変換する.
 *
 * Returned value は `0..ALL_PERMS=7` の 8 通り (Phase 7 Task 7-5 で CLEAR が
 * 追加され、4 → 8 に拡張).
 *
 * @example
 *   permissionsBitflagFromBooleans({ kick: true,  imagepaste: false, clear: false }) === 1
 *   permissionsBitflagFromBooleans({ kick: false, imagepaste: true,  clear: false }) === 2
 *   permissionsBitflagFromBooleans({ kick: false, imagepaste: false, clear: true  }) === 4
 *   permissionsBitflagFromBooleans({ kick: true,  imagepaste: true,  clear: true  }) === 7
 */
export const permissionsBitflagFromBooleans = (input: PermissionBooleans): number => {
  let perms = 0;
  if (input.kick) perms |= Perm.KICK;
  if (input.imagepaste) perms |= Perm.IMAGEPASTE;
  if (input.clear) perms |= Perm.CLEAR;
  return perms;
};

/**
 * Server から返ってきた bitflag を CB 値 3 つに分解する (一覧表示のバッジ用).
 *
 * `ALL_PERMS` を超えるビットは無視 (forward-compat: 将来 flag が増えても
 * 既知 3 flag のみ抽出する). 負数 / NaN / undefined は全 false.
 */
export const permissionsBooleansFromBitflag = (perms: number): PermissionBooleans => {
  if (!Number.isFinite(perms) || perms < 0) {
    return { kick: false, imagepaste: false, clear: false };
  }
  // Defensive — clip to ALL_PERMS so未知ビットが拾われない.
  const clipped = perms & ALL_PERMS;
  return {
    kick: (clipped & Perm.KICK) === Perm.KICK,
    imagepaste: (clipped & Perm.IMAGEPASTE) === Perm.IMAGEPASTE,
    clear: (clipped & Perm.CLEAR) === Perm.CLEAR,
  };
};

/* -----------------------------------------------------------------------------
 * canClearWhiteboard — UI gate for the「全消し」(bomb) button (Phase 6.8-A).
 *
 * Returns true iff the caller should be allowed to issue `whiteboard.clear`:
 *   - `Perm.CLEAR` 保有, OR
 *   - `room.clearOpen === true` (オープン運用ルーム — 誰でも clear 可能).
 *
 * server (`WhiteboardService.handleClear`) が同じ条件で再判定するので, UI
 * 側の表示と server 側の真の判定は冗長 (UI 漏れがあっても server で止まる).
 * `MemberPanel.canKickMember` と同じ流儀で application 層の純粋関数として
 * 切り出し, vitest が node 環境でテストできるようにする.
 * --------------------------------------------------------------------------- */

export const canClearWhiteboard = (permissions: number, clearOpen: boolean): boolean => {
  if (clearOpen) return true;
  return hasPerm(permissions, Perm.CLEAR);
};

/* -----------------------------------------------------------------------------
 * formatCaptureTimestamp — `oekaki-yyyymmdd-HHmmss.png` 風のローカル時刻文字列
 * を生成する (Phase 6.8-A Task 2).
 *
 * - ロケール非依存 (`toLocaleString` を使わず `getFullYear` / `getMonth` ... を
 *   合成). UTC ではなく **ローカルタイムゾーン** で出す (= ユーザの実時刻に
 *   揃える).
 * - 形式: `YYYYMMDD-HHmmss`. ファイル名としての禁止文字 (`:` 等) を含めない.
 * - 純粋関数なので vitest が node 環境でテスト可能.
 * --------------------------------------------------------------------------- */

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

export const formatCaptureTimestamp = (date: Date): string => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mm = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  return `${y}${m}${d}-${hh}${mm}${ss}`;
};

/* -----------------------------------------------------------------------------
 * shouldDispatchChatMessage — future-only chat filter (ユーザ確認 2026-05-06).
 *
 * Phase 7 Task 7-10b の元実装は ChatPanel 側で「render 段で filter」する非対称
 * 設計だった (drawing engine は future-only / chat は過去ログ復活アリ). ユーザ
 * 確認で「ChatPanel もフィルタ中のメッセージは解除しても復活しない」へ仕様
 * 統一 (drawing engine `setStrokeFilter` と同方針).
 *
 * SessionProvider が `chat.message` を受信した時点で本関数で gate し,
 * `false` を返したら dispatch を skip して reducer の `view.chat` に入れない.
 * フィルタ解除しても state には残らないため復活しない.
 *
 * 自分の message は絶対 filter しない (UI 上も `canShowFilterToggle` で除外
 * 済だが防御的にここでも skip). selfUid が null (= pre-join) の場合も
 * filter は適用するが、現実的には pre-join 中に chat.message は来ない.
 *
 * @returns `true` なら dispatch して reducer に入れる, `false` なら捨てる.
 * --------------------------------------------------------------------------- */

export const shouldDispatchChatMessage = (
  event: { uid: string },
  selfUid: string | null,
  filteredUids: ReadonlySet<string>,
): boolean => {
  if (selfUid !== null && event.uid === selfUid) return true;
  return !filteredUids.has(event.uid);
};
